import {
    ObjectAnalyticsObjectProperty,
} from '@/object-analytics/aggregations/object-analytics-object-property.helper';
import {
    ObjectLabelOverridesMap,
    ObjectLabels,
    getEffectiveLabels,
} from '@/object-analytics/object-label.helper';
import {
    FUNNEL_PRIMARY_KEY_TYPE,
    FunnelPrimaryKey,
    FunnelPrimaryKeyType,
    resolveFunnelPrimaryKey,
} from '@/reports/funnel/funnel-primary-key.helper';
import { MetadataTypeId } from '@/types/analytics/analytics.types';
import { i18n_t as $t } from '@/utils/i18n';
import { iterateThroughMetadata } from '@/vuex/modules/metadata/metadata.utils';

interface FunnelConfigLike {
    primaryKey?: FunnelPrimaryKey;
}

export function getFunnelPrimaryKey (config: FunnelConfigLike | undefined | null): FunnelPrimaryKey {
    return config?.primaryKey || { type: FUNNEL_PRIMARY_KEY_TYPE.VISITOR };
}

export function isObjectPrimaryKey (config: FunnelConfigLike | undefined | null): boolean {
    return resolveFunnelPrimaryKey(config).type === FUNNEL_PRIMARY_KEY_TYPE.OBJECT;
}

interface ObjectPropertyTimestampSources {
    metadata?: Record<string, Record<string, Record<string, { objectProperty?: number | null }>>> | null;
    historicalMetadata?: Array<{
        field?: string;
        group?: string;
        kind?: string;
        objectProperty?: number | null;
    }> | null;
}

// Looks up the live "promoted to object property" timestamp for a funnel's
// object-property primary key. Returns null for visitor/account keys or when
// the property is no longer flagged as an object property in metadata.
export function getFunnelPrimaryKeyObjectPropertyTimestamp (
    primaryKey: FunnelPrimaryKey | undefined | null,
    sources: ObjectPropertyTimestampSources,
): number | null {
    if (!primaryKey || primaryKey.type !== FUNNEL_PRIMARY_KEY_TYPE.OBJECT) return null;

    const { property } = primaryKey;
    if (property.kind === MetadataTypeId.Historical) {
        const match = (sources.historicalMetadata || []).find(
            (item) =>
                item.field === property.field &&
                (item.group || undefined) === (property.group || undefined) &&
                (item.kind || undefined) === (property.metadataKind || undefined),
        );

        return typeof match?.objectProperty === 'number' ? match.objectProperty : null;
    }

    const eventProperties: Array<{ field: string; group?: string; objectProperty?: number | null }> =
        iterateThroughMetadata(sources.metadata?.event, MetadataTypeId.Event);
    const match = eventProperties.find(
        (item) => item.field === property.field && (item.group || undefined) === (property.group || undefined),
    );

    return typeof match?.objectProperty === 'number' ? match.objectProperty : null;
}

export interface FunnelPrimaryKeyOption {
    id: string;
    label: string;
    type: FunnelPrimaryKeyType;
    property?: ObjectAnalyticsObjectProperty;
    section?: string;
    stale?: boolean;
}

interface MetadataItem {
    DisplayName?: string;
    objectProperty?: number | null;
}

interface IteratedMetadataItem extends MetadataItem {
    field: string;
    group?: string;
}

interface HistoricalMetadataItem {
    field: string;
    DisplayName?: string;
    group?: string;
    kind?: string;
    objectProperty?: number | null;
}

interface BuildOptionsParams {
    selected: FunnelPrimaryKey;
    metadata: Record<string, Record<string, Record<string, MetadataItem>>> | undefined | null;
    historicalMetadata: HistoricalMetadataItem[] | undefined | null;
    visitorLabel: string;
    visitorSection: string;
    // The selector groups every object-property option (event property,
    // historical metadata, future kinds) under a single section. The
    // event-property / historical-metadata distinction is an internal
    // storage detail — only object properties are selectable, so a single
    // "Object property" header reflects what the user is actually picking.
    objectSection: string;
    // Same per-field label overrides OA persists via the
    // `objectAnalyticsLabelOverrides` user setting. When an option's field has
    // an entry here, the row's label uses the customer's customized singular
    // noun as-entered so the funnel selector matches OA's terminology.
    labelOverrides?: ObjectLabelOverridesMap | null;
}

function makeObjectId (property: ObjectAnalyticsObjectProperty): string {
    const kind = property.kind || MetadataTypeId.Event;
    const metadataKind = property.metadataKind || '';
    const group = property.group || '';

    return `${kind}::${metadataKind}::${group}::${property.field}`;
}

// Pluralization is locale- and noun-specific; we don't synthesize a plural
// from the singular (English-style "+ s" produces "Orderss" / "Sessionss" for
// nouns already ending in s, and is wrong for irregular plurals and non-English
// locales). Customers configure the plural via the OA label-override modal —
// until they do, surfaces fall back to the singular as the plural.
function buildFallbackLabels (property: ObjectAnalyticsObjectProperty): ObjectLabels {
    const singular = property.label || property.field;

    return {
        pageTitle: singular,
        singular,
        plural: singular,
    };
}

function makeOptionFromObjectProperty (
    property: ObjectAnalyticsObjectProperty,
    section: string,
    labelOverrides: ObjectLabelOverridesMap | null | undefined,
    stale = false,
): FunnelPrimaryKeyOption {
    const resolved = getEffectiveLabels(property.field, labelOverrides, buildFallbackLabels(property));

    return {
        id: makeObjectId(property),
        label: resolved.singular,
        type: FUNNEL_PRIMARY_KEY_TYPE.OBJECT,
        property,
        section,
        stale,
    };
}

// Returns the selector option list for the configured funnel:
//   Visitor (always), Account (when allowed), then every event property +
//   historical metadata item currently marked as an object property
//   (`objectProperty` truthy). If the saved selection is no longer in that
//   live set, it's appended as a stale entry so the funnel still renders the
//   user's choice (with a warning) and can keep running on existing data.
export function buildFunnelPrimaryKeyOptions ({
    selected,
    metadata,
    historicalMetadata,
    visitorLabel,
    visitorSection,
    objectSection,
    labelOverrides,
}: BuildOptionsParams): FunnelPrimaryKeyOption[] {
    // Account is intentionally absent: the funnel agg cannot currently support
    // accountId as a primary key. The discriminated union still includes
    // `'account'` for the eventual "per-object account metric" use case, but
    // the selector offers only visitor + object-property options.
    const options: FunnelPrimaryKeyOption[] = [
        {
            id: FUNNEL_PRIMARY_KEY_TYPE.VISITOR,
            label: visitorLabel,
            type: FUNNEL_PRIMARY_KEY_TYPE.VISITOR,
            section: visitorSection,
        },
    ];

    const eventProperties: IteratedMetadataItem[] = iterateThroughMetadata(metadata?.event, MetadataTypeId.Event);
    eventProperties
        .filter((item) => Boolean(item.objectProperty))
        .forEach((item) => {
            const property: ObjectAnalyticsObjectProperty = {
                field: item.field,
                kind: MetadataTypeId.Event,
                group: item.group,
                label: item.DisplayName,
            };
            options.push(makeOptionFromObjectProperty(property, objectSection, labelOverrides));
        });

    (historicalMetadata || [])
        .filter((item) => Boolean(item.objectProperty))
        .forEach((item) => {
            const property: ObjectAnalyticsObjectProperty = {
                field: item.field,
                kind: MetadataTypeId.Historical,
                group: item.group,
                metadataKind: item.kind,
                label: item.DisplayName,
            };
            options.push(makeOptionFromObjectProperty(property, objectSection, labelOverrides));
        });

    if (selected.type === FUNNEL_PRIMARY_KEY_TYPE.OBJECT) {
        const selectedId = makeObjectId(selected.property);
        const found = options.some((option) => option.id === selectedId);
        if (!found) {
            options.push(makeOptionFromObjectProperty(selected.property, objectSection, labelOverrides, true));
        }
    }

    return options;
}

export function findSelectedOption (
    options: FunnelPrimaryKeyOption[],
    selected: FunnelPrimaryKey,
): FunnelPrimaryKeyOption | undefined {
    if (selected.type === FUNNEL_PRIMARY_KEY_TYPE.OBJECT) {
        const selectedId = makeObjectId(selected.property);

        return options.find((option) => option.id === selectedId);
    }

    return options.find((option) => option.type === selected.type);
}

export function primaryKeyFromOption (option: FunnelPrimaryKeyOption): FunnelPrimaryKey {
    if (option.type === FUNNEL_PRIMARY_KEY_TYPE.OBJECT && option.property) {
        return { type: FUNNEL_PRIMARY_KEY_TYPE.OBJECT, property: option.property };
    }
    if (option.type === FUNNEL_PRIMARY_KEY_TYPE.ACCOUNT) {
        return { type: FUNNEL_PRIMARY_KEY_TYPE.ACCOUNT };
    }

    return { type: FUNNEL_PRIMARY_KEY_TYPE.VISITOR };
}

// Locale-resolvable default labels for each primary key type. Callers pass
// the user's translated visitor/account strings so the funnel matches the
// rest of the UI; the object-property singular/plural come from the OA
// label-override map (same source the OA page uses) so customers who have
// renamed "objects" → "projects" / "tickets" see those nouns in the funnel
// table headers too.
export interface FunnelPrimaryKeyLabelDefaults {
    visitorSingular: string;
    visitorPlural: string;
    accountSingular: string;
    accountPlural: string;
    objectSingularFallback: string;
    objectPluralFallback: string;
}

export interface FunnelPrimaryKeyLabels {
    singular: string;
    plural: string;
}

// "<noun> ID" suffix used by table + CSV headers. Object-property singulars
// already commonly end in "ID" (e.g. "Order ID"), so a naive ` ID` append
// produces "Order ID ID" — detect the trailing token and skip the suffix.
export function buildPrimaryKeyIdColumnLabel (singular: string): string {
    return /\bID\s*$/i.test(singular) ? singular : $t('funnelQueryBuilder.{noun} ID', { noun: singular });
}

export function getFunnelPrimaryKeyLabels (
    primaryKey: FunnelPrimaryKey,
    {
        labelOverrides,
        defaults,
    }: { labelOverrides?: ObjectLabelOverridesMap | null; defaults: FunnelPrimaryKeyLabelDefaults },
): FunnelPrimaryKeyLabels {
    if (primaryKey.type === FUNNEL_PRIMARY_KEY_TYPE.ACCOUNT) {
        return { singular: defaults.accountSingular, plural: defaults.accountPlural };
    }

    if (primaryKey.type === FUNNEL_PRIMARY_KEY_TYPE.OBJECT) {
        const singular = primaryKey.property.label || primaryKey.property.field || defaults.objectSingularFallback;
        // Pluralization is locale- and noun-specific; falling back to the
        // singular avoids hardcoding "+ s" (which breaks for nouns already
        // ending in s, irregular plurals, and non-English locales). Customers
        // configure the plural via the OA label-override modal.
        const fallback: ObjectLabels = {
            pageTitle: defaults.objectPluralFallback,
            singular,
            plural: singular,
        };
        const resolved = getEffectiveLabels(primaryKey.property.field, labelOverrides, fallback);

        return { singular: resolved.singular, plural: resolved.plural };
    }

    return { singular: defaults.visitorSingular, plural: defaults.visitorPlural };
}
